import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  Table,
  Spinner,
  Badge,
  HStack,
  VStack,
  Card,
  Alert,
  Link,
} from '@chakra-ui/react';
import { Gift, RefreshCw } from 'lucide-react';
import api from '../api';

function DailyQuestRewardRecovery() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [distributeResult, setDistributeResult] = useState(null);

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['daily-quest-missing-rewards'],
    queryFn: async () => {
      const res = await api.get('/quests/missing-daily-rewards');
      return res.data;
    },
    staleTime: 30000,
  });

  const distributeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/quests/distribute-missing-rewards');
      return res.data;
    },
    onSuccess: (result) => {
      setDistributeResult(result);
      queryClient.invalidateQueries({ queryKey: ['daily-quest-missing-rewards'] });
    },
    onError: (err) => {
      setDistributeResult({
        distributed: 0,
        errors: [{ error: err.response?.data?.error || err.message }],
      });
    },
  });

  const missingRewards = data?.missingRewards ?? [];
  const summary = data?.summary ?? { totalUsers: 0, totalMissing: 0 };

  if (error) {
    return (
      <Box p="6" maxW="1400px" mx="auto">
        <Alert.Root status="error" colorPalette="red">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Failed to load</Alert.Title>
            <Alert.Description>{error.message}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
        <Button mt="4" variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw size={16} />
          <Text as="span" ml="2">Retry</Text>
        </Button>
      </Box>
    );
  }

  return (
    <Box p="6" maxW="1400px" mx="auto">
      <VStack gap="6" align="stretch">
        <HStack justify="space-between" align="center" flexWrap="wrap" gap="4">
          <Heading size="lg">Daily Quest Reward Recovery</Heading>
          <HStack gap="2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw size={16} />
              <Text as="span" ml="2">Refresh</Text>
            </Button>
            <Button
              size="sm"
              colorPalette="green"
              onClick={() => distributeMutation.mutate()}
              disabled={missingRewards.length === 0 || distributeMutation.isPending}
            >
              <Gift size={16} />
              <Text as="span" ml="2">Distribute All</Text>
            </Button>
          </HStack>
        </HStack>

        <Text color="fg.muted" fontSize="sm">
          Finds users who completed daily quests (Heroic Harvest, etc.) but did not receive rewards
          due to a previous bug. Click Distribute to grant missing rewards to their default inventory.
        </Text>

        {distributeResult && (
          <Alert.Root
            status={distributeResult.errors?.length > 0 ? 'warning' : 'success'}
            colorPalette={distributeResult.errors?.length > 0 ? 'orange' : 'green'}
          >
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>
                Distributed {distributeResult.distributed} user-quest pairs
                {distributeResult.errors?.length > 0 &&
                  ` (${distributeResult.errors.length} errors)`}
              </Alert.Title>
              {distributeResult.errors?.length > 0 && (
                <Alert.Description as="div" fontSize="xs" mt="2" overflow="auto" maxH="120px">
                  {distributeResult.errors.map((e, i) => (
                    <Text key={i} as="span" display="block">
                      {e.userId} / {e.questId}: {e.error}
                    </Text>
                  ))}
                </Alert.Description>
              )}
            </Alert.Content>
          </Alert.Root>
        )}

        <Card.Root variant="outline" borderWidth="1px" borderColor="border">
          <Card.Header>
            <HStack justify="space-between">
              <Text fontWeight="semibold">Missing rewards</Text>
              <Badge colorPalette="orange" size="sm">
                {summary.totalUsers} users · {summary.totalMissing} missing
              </Badge>
            </HStack>
          </Card.Header>
          <Card.Body pt="0">
            {isLoading ? (
              <HStack justify="center" py="10">
                <Spinner size="lg" />
              </HStack>
            ) : missingRewards.length === 0 ? (
              <Text color="fg.muted" py="6" textAlign="center">
                No missing daily quest rewards found.
              </Text>
            ) : (
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>User</Table.ColumnHeader>
                    <Table.ColumnHeader>Quest</Table.ColumnHeader>
                    <Table.ColumnHeader>Quest Line</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">Missing</Table.ColumnHeader>
                    <Table.ColumnHeader>Rewards</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {missingRewards.map((m, idx) => (
                    <Table.Row key={`${m.userId}-${m.questId}-${idx}`}>
                      <Table.Cell>
                        <VStack gap="0" align="start">
                          <Link
                            as="button"
                            fontWeight="medium"
                            textDecoration="underline"
                            colorPalette="cyan"
                            _hover={{ opacity: 0.9 }}
                            onClick={() => navigate(`/user/${m.userId}`)}
                          >
                            {m.userName || '—'}
                          </Link>
                          <Text fontSize="xs" color="fg.muted">{m.userId}</Text>
                        </VStack>
                      </Table.Cell>
                      <Table.Cell>{m.questTitle || m.questId}</Table.Cell>
                      <Table.Cell>{m.questLineName}</Table.Cell>
                      <Table.Cell textAlign="end">{m.missingCount}</Table.Cell>
                      <Table.Cell>
                        <HStack gap="1" flexWrap="wrap">
                          {Array.isArray(m.rewards) &&
                            m.rewards.map((r, i) => (
                              <Badge key={i} size="sm" variant="subtle">
                                {r.assetId || r.targetId} × {(r.amount || 1) * m.missingCount}
                              </Badge>
                            ))}
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
}

export default DailyQuestRewardRecovery;
